import { Renderer } from './renderer'
import { HandTracker } from './hand-tracker'
import { FaceTracker } from './face-tracker'
import { ParticleSystem, ParticleShape, RingShape } from './particle-system'
import { GravityWell } from './gravity-well'
import './styles.css'

type InputMethod = 'hands' | 'face'

// Common interface for both trackers
interface Tracker {
  initialize(): Promise<void>
  getHands(): { x: number; y: number; z: number; size: number }[]
  dispose(): void
}

class GravityGame {
  private renderer: Renderer
  private tracker: Tracker | null = null
  private particleSystem: ParticleSystem | null = null
  private gravityWells: GravityWell[] = []

  private isRunning = false
  private lastHandsDetectedTime = 0
  private readonly NO_HAND_TIMEOUT = 2000
  private readonly NO_HAND_PROMPT_TIMEOUT = 5000

  // Settings
  private inputMethod: InputMethod = 'hands'
  private particleShape: ParticleShape = 'glow'
  private ringShape: RingShape = 'circle'

  // FPS tracking
  private frameCount = 0
  private lastFpsUpdate = 0
  private currentFps = 0

  // Debug overlay elements
  private debugFps: HTMLElement | null = null
  private debugParticles: HTMLElement | null = null
  private debugMaxVel: HTMLElement | null = null
  private debugAvgVel: HTMLElement | null = null
  private debugZoneFar: HTMLElement | null = null
  private debugZoneOrbit: HTMLElement | null = null
  private debugZoneDisk: HTMLElement | null = null
  private debugZoneCore: HTMLElement | null = null
  private debugScreen: HTMLElement | null = null
  private debugVideo: HTMLElement | null = null
  private debugScale: HTMLElement | null = null
  private debugCrop: HTMLElement | null = null

  constructor() {
    this.renderer = new Renderer()
    this.setupUI()
    this.setupDebugOverlay()
  }

  private setupUI(): void {
    const startBtn = document.getElementById('start-btn')
    if (startBtn) {
      startBtn.addEventListener('click', () => this.start())
    }

    // Setup toggle buttons for input method
    const inputMethodGroup = document.getElementById('input-method')
    if (inputMethodGroup) {
      inputMethodGroup.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        if (target.classList.contains('toggle-btn')) {
          inputMethodGroup.querySelectorAll('.toggle-btn').forEach(btn => btn.classList.remove('active'))
          target.classList.add('active')
          this.inputMethod = target.dataset.value as InputMethod
        }
      })
    }

    // Setup toggle buttons for particle shape
    const particleShapeGroup = document.getElementById('particle-shape')
    if (particleShapeGroup) {
      particleShapeGroup.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        if (target.classList.contains('toggle-btn')) {
          particleShapeGroup.querySelectorAll('.toggle-btn').forEach(btn => btn.classList.remove('active'))
          target.classList.add('active')
          this.particleShape = target.dataset.value as ParticleShape
        }
      })
    }

    // Setup toggle buttons for ring shape
    const ringShapeGroup = document.getElementById('ring-shape')
    if (ringShapeGroup) {
      ringShapeGroup.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        if (target.classList.contains('toggle-btn')) {
          ringShapeGroup.querySelectorAll('.toggle-btn').forEach(btn => btn.classList.remove('active'))
          target.classList.add('active')
          this.ringShape = target.dataset.value as RingShape
        }
      })
    }
  }

  private setupDebugOverlay(): void {
    this.debugFps = document.getElementById('debug-fps')
    this.debugParticles = document.getElementById('debug-particles')
    this.debugMaxVel = document.getElementById('debug-max-vel')
    this.debugAvgVel = document.getElementById('debug-avg-vel')
    this.debugZoneFar = document.getElementById('debug-zone-far')
    this.debugZoneOrbit = document.getElementById('debug-zone-orbit')
    this.debugZoneDisk = document.getElementById('debug-zone-disk')
    this.debugZoneCore = document.getElementById('debug-zone-core')
    this.debugScreen = document.getElementById('debug-screen')
    this.debugVideo = document.getElementById('debug-video')
    this.debugScale = document.getElementById('debug-scale')
    this.debugCrop = document.getElementById('debug-crop')

    // Toggle debug overlay with 'D' key
    const debugOverlay = document.getElementById('debug-overlay')
    window.addEventListener('keydown', (e) => {
      if (e.key === 'd' || e.key === 'D') {
        debugOverlay?.classList.toggle('hidden')
      }
    })
  }

  private async start(): Promise<void> {
    const entryScreen = document.getElementById('entry-screen')
    const handIndicator = document.getElementById('hand-indicator')

    try {
      // Create particle system with selected settings
      this.particleSystem = new ParticleSystem(
        this.renderer.scene,
        this.particleShape,
        this.ringShape
      )

      // Show initial particle count
      const metrics = this.particleSystem.getMetrics()
      if (this.debugParticles) {
        this.debugParticles.textContent = metrics.particleCount.toString()
      }

      // Create appropriate tracker based on input method
      this.tracker = this.inputMethod === 'face' ? new FaceTracker() : new HandTracker()
      await this.tracker.initialize()

      entryScreen?.classList.add('hidden')
      handIndicator?.classList.remove('hidden')

      this.isRunning = true
      this.lastHandsDetectedTime = performance.now()
      this.lastFpsUpdate = performance.now()
      this.animate()

    } catch (error) {
      console.error('Failed to start:', error)
      alert('Failed to access camera. Please ensure camera permissions are granted.')
    }
  }

  private animate = (): void => {
    if (!this.isRunning) return

    requestAnimationFrame(this.animate)

    // Update FPS counter
    this.frameCount++
    const now = performance.now()
    if (now - this.lastFpsUpdate >= 500) { // Update every 500ms
      this.currentFps = Math.round(this.frameCount * 1000 / (now - this.lastFpsUpdate))
      this.frameCount = 0
      this.lastFpsUpdate = now
      this.updateDebugOverlay()
    }

    // Update tracking (hands or face)
    if (this.tracker) {
      const positions = this.tracker.getHands()
      this.updateGravityWells(positions)
      this.updateHandIndicator(positions.length)
      this.checkNoHandState(positions.length)
    }

    // Update particles with gravity wells
    if (this.particleSystem) {
      this.particleSystem.update(this.gravityWells)
    }

    // Render
    this.renderer.render()
  }

  private updateDebugOverlay(): void {
    if (!this.particleSystem) return
    const metrics = this.particleSystem.getMetrics()

    if (this.debugFps) {
      this.debugFps.textContent = this.currentFps.toString()
    }
    if (this.debugMaxVel) {
      this.debugMaxVel.textContent = metrics.maxVelocity.toFixed(1)
    }
    if (this.debugAvgVel) {
      this.debugAvgVel.textContent = metrics.avgVelocity.toFixed(1)
    }

    // Zone distribution
    const zones = metrics.zoneDistribution
    const total = metrics.particleCount
    if (this.debugZoneFar) {
      this.debugZoneFar.textContent = `${zones.far} (${Math.round(zones.far / total * 100)}%)`
    }
    if (this.debugZoneOrbit) {
      this.debugZoneOrbit.textContent = `${zones.orbit} (${Math.round(zones.orbit / total * 100)}%)`
    }
    if (this.debugZoneDisk) {
      this.debugZoneDisk.textContent = `${zones.disk} (${Math.round(zones.disk / total * 100)}%)`
    }
    if (this.debugZoneCore) {
      this.debugZoneCore.textContent = `${zones.core} (${Math.round(zones.core / total * 100)}%)`
    }
  }

  private updateGravityWells(hands: { x: number; y: number; z: number; size: number }[]): void {
    while (this.gravityWells.length < hands.length) {
      this.gravityWells.push(new GravityWell())
    }

    const video = document.getElementById('webcam') as HTMLVideoElement
    if (!video) return

    // Video's intrinsic dimensions
    const videoWidth = video.videoWidth || 640
    const videoHeight = video.videoHeight || 480

    // Video element's rendered dimensions and position
    const rect = video.getBoundingClientRect()

    // Calculate how object-fit: cover scales the video
    const videoAspect = videoWidth / videoHeight
    const elemAspect = rect.width / rect.height

    let scale: number
    let cropX = 0  // Amount cropped from left (in screen pixels)
    let cropY = 0  // Amount cropped from top (in screen pixels)

    if (elemAspect > videoAspect) {
      // Element is wider - video scaled to fit width, cropped vertically
      scale = rect.width / videoWidth
      const scaledHeight = videoHeight * scale
      cropY = (scaledHeight - rect.height) / 2
    } else {
      // Element is taller - video scaled to fit height, cropped horizontally
      scale = rect.height / videoHeight
      const scaledWidth = videoWidth * scale
      cropX = (scaledWidth - rect.width) / 2
    }

    // Update debug info
    if (this.debugScreen) {
      this.debugScreen.textContent = `${window.innerWidth}x${window.innerHeight}`
    }
    if (this.debugVideo) {
      this.debugVideo.textContent = `${videoWidth}x${videoHeight}`
    }
    if (this.debugScale) {
      this.debugScale.textContent = scale.toFixed(3)
    }
    if (this.debugCrop) {
      this.debugCrop.textContent = `X:${Math.round(cropX)} Y:${Math.round(cropY)}`
    }

    for (let i = 0; i < hands.length; i++) {
      const hand = hands[i]
      if (hand) {
        // Hand position in scaled video coordinates
        const scaledX = hand.x * videoWidth * scale
        const scaledY = hand.y * videoHeight * scale

        // Position within element's visible area (after crop)
        const elemX = scaledX - cropX
        const elemY = scaledY - cropY

        // Screen position: CSS transform: scaleX(-1) mirrors the video visually,
        // but MediaPipe coords are in original frame, so we mirror to match visual
        const screenX = rect.left + rect.width - elemX
        const screenY = rect.top + elemY

        this.gravityWells[i]?.setPosition(screenX, screenY, hand.z, hand.size)
        this.gravityWells[i]?.setActive(true)
      }
    }

    for (let i = hands.length; i < this.gravityWells.length; i++) {
      this.gravityWells[i]?.setActive(false)
    }
  }

  private updateHandIndicator(count: number): void {
    const handCountEl = document.getElementById('hand-count')
    if (handCountEl) {
      const label = this.inputMethod === 'face' ? 'Faces' : 'Hands'
      handCountEl.textContent = `${label}: ${count}`
    }
  }

  private checkNoHandState(handCount: number): void {
    const now = performance.now()
    const noHandPrompt = document.getElementById('no-hand-prompt')

    if (handCount > 0) {
      this.lastHandsDetectedTime = now
      noHandPrompt?.classList.add('hidden')
      this.particleSystem?.setDriftMode(false)
    } else {
      const elapsed = now - this.lastHandsDetectedTime

      if (elapsed > this.NO_HAND_TIMEOUT) {
        this.particleSystem?.setDriftMode(true)
      }

      if (elapsed > this.NO_HAND_PROMPT_TIMEOUT) {
        noHandPrompt?.classList.remove('hidden')
      }
    }
  }

  public dispose(): void {
    this.isRunning = false
    this.tracker?.dispose()
    this.renderer.dispose()
  }
}

// Initialize the game
const game = new GravityGame()

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  game.dispose()
})
